import { memo, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection, SectionHeader } from '@factorialco/f0-react/dist/experimental'

import {
  TicketingResolverTeamsDocument,
  type TicketingResolverTeamsQuery,
  TicketingTicketCategoriesDocument,
  type TicketingTicketCategoriesQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import i18n from 'modules/core/lib/i18n'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import CategoryFormDialog from './CategoryFormDialog'
import DeleteCategoryDialog from './DeleteCategoryDialog'
import { buildCategoryItemActions } from './buildCategoryItemActions'

type CategoryNode =
  TicketingTicketCategoriesQuery['ticketing']['ticketCategoriesConnection']['nodes'][number]

const PRIORITY_LEVEL_LABELS: Record<string, () => string> = {
  critical: () => i18n.t('ticketing.settings.categories.form.priority_levels.critical'),
  high: () => i18n.t('ticketing.settings.categories.form.priority_levels.high'),
  medium: () => i18n.t('ticketing.settings.categories.form.priority_levels.medium'),
  low: () => i18n.t('ticketing.settings.categories.form.priority_levels.low'),
}

const formatPriority = (cat: CategoryNode) => {
  switch (cat.priorityMode) {
    case 'fixed':
      return cat.fixedPriorityLevel
        ? i18n.t('ticketing.settings.categories.priority_fixed_with_level', {
            level: PRIORITY_LEVEL_LABELS[cat.fixedPriorityLevel]?.() ?? cat.fixedPriorityLevel,
          })
        : '—'
    case 'manual':
      return i18n.t('ticketing.settings.categories.form.priority_modes.manual')
    default:
      return '—'
  }
}

const formatSla = (cat: CategoryNode) => {
  switch (cat.slaMode) {
    case 'fixed_sla':
      return cat.slaFixedHours != null ? `${cat.slaFixedHours}h` : '—'
    case 'sla_per_priority':
      return i18n.t('ticketing.settings.categories.form.sla_modes.sla_per_priority')
    default:
      return '—'
  }
}

const formatAssignee = (cat: CategoryNode) => {
  switch (cat.assignmentMode) {
    case 'specific_member':
      return cat.defaultAssigneeName ?? '—'
    case 'lowest_workload':
      return i18n.t('ticketing.settings.categories.form.assignment_modes.lowest_workload')
    default:
      return i18n.t('ticketing.settings.categories.form.assignment_modes.unassigned')
  }
}

const TicketCategoriesSection = () => {
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [categoryToEdit, setCategoryToEdit] = useState<CategoryNode | null>(null)
  const [categoryToDelete, setCategoryToDelete] = useState<CategoryNode | null>(null)

  const client = useApolloClient()
  const navigate = useNavigate()
  const { has_dev_ai_ticketing_workflows_co_creation } = useFeatures([
    'dev_ai_ticketing_workflows_co_creation',
  ])

  const source = useGraphqlDataCollectionSource(
    {
      document: TicketingTicketCategoriesDocument,
      mapVariables: ({ search, sortings, filters }) => {
        const resolverTeamFilter =
          filters?.resolverTeamId && filters.resolverTeamId.length > 0
            ? { resolverTeamId: { in: filters.resolverTeamId } }
            : undefined

        const nameFilter = search ? { name: { contains: search } } : undefined

        return {
          filter: { ...nameFilter, ...resolverTeamFilter },
          sortOrder: sortings?.[0],
        }
      },
      includePaginationVars: true,
      paginationType: 'pages' as const,
      perPage: 20,
      mapData: (data: TicketingTicketCategoriesQuery) =>
        data.ticketing.ticketCategoriesConnection.nodes,
      mapPagination: (data: TicketingTicketCategoriesQuery) => ({
        total: data.ticketing.ticketCategoriesConnection.totalCount ?? 0,
      }),
      search: {
        enabled: true,
      },
      defaultSortings: {
        field: 'name' as const,
        order: 'asc' as const,
      },
      filters: {
        resolverTeamId: {
          label: i18n.t('ticketing.settings.categories.col_team'),
          type: 'in' as const,
          options: {
            cache: true,
            options: async () => {
              const result = await client.query({
                query: TicketingResolverTeamsDocument,
                variables: {
                  page: 1,
                  perPage: 1000,
                },
                fetchPolicy: 'cache-first',
              })
              return (
                result.data?.ticketing?.resolverTeamsConnection?.nodes?.map(
                  (
                    node: TicketingResolverTeamsQuery['ticketing']['resolverTeamsConnection']['nodes'][number]
                  ) => ({
                    label: node.teamName,
                    value: String(node.id),
                  })
                ) ?? []
              )
            },
          },
        },
      },
      itemActions: (item: CategoryNode) =>
        buildCategoryItemActions({
          item,
          navigate,
          onEdit: setCategoryToEdit,
          onDelete: setCategoryToDelete,
          showWorkflowAction: has_dev_ai_ticketing_workflows_co_creation,
        }),
    },
    []
  )

  return (
    <ResponsivePage.Body showHeader={false}>
      <Box paddingY="s24" gap="s24">
        <SectionHeader
          title={i18n.t('ticketing.settings.categories.title')}
          description={i18n.t('ticketing.settings.categories.description')}
          action={{
            onClick: () => setShowCreateDialog(true),
            label: i18n.t('ticketing.settings.categories.create_button'),
          }}
        />

        <OneDataCollection
          source={source}
          emptyStates={{
            'no-data': {
              emoji: '🏷️',
              title: i18n.t('ticketing.settings.categories.empty_title'),
              description: i18n.t('ticketing.settings.categories.empty_description'),
            },
            error: {
              title: i18n.t('ticketing.settings.categories.error_title'),
              description: i18n.t('ticketing.settings.categories.error_description'),
            },
          }}
          visualizations={[
            {
              type: 'table',
              options: {
                columns: [
                  {
                    label: i18n.t('ticketing.settings.categories.col_name'),
                    render: (item: CategoryNode) => item.name,
                    sorting: 'name',
                  },
                  {
                    label: i18n.t('ticketing.settings.categories.col_team'),
                    render: (item: CategoryNode) => ({
                      type: 'team' as const,
                      value: {
                        name: item.resolverTeamName,
                        src: '',
                      },
                    }),
                    sorting: 'resolverTeamName',
                  },
                  {
                    label: i18n.t('ticketing.settings.categories.col_assignee'),
                    render: (item: CategoryNode) => formatAssignee(item),
                    sorting: 'assignmentMode',
                  },
                  {
                    label: i18n.t('ticketing.settings.categories.col_priority'),
                    render: (item: CategoryNode) => formatPriority(item),
                    sorting: 'priorityMode',
                  },
                  {
                    label: i18n.t('ticketing.settings.categories.col_sla'),
                    render: (item: CategoryNode) => formatSla(item),
                    sorting: 'slaMode',
                  },
                  {
                    label: i18n.t('ticketing.settings.categories.form.ai_suggestions_mode'),
                    render: (item: CategoryNode) => {
                      const mode = item.aiSuggestionsMode ?? 'automatic'
                      switch (mode) {
                        case 'automatic':
                          return i18n.t(
                            'ticketing.settings.categories.form.ai_suggestions_modes.automatic'
                          )
                        case 'on_demand':
                          return i18n.t(
                            'ticketing.settings.categories.form.ai_suggestions_modes.on_demand'
                          )
                        case 'disabled':
                          return i18n.t(
                            'ticketing.settings.categories.form.ai_suggestions_modes.disabled'
                          )
                        default:
                          return '—'
                      }
                    },
                    sorting: 'aiSuggestionsMode',
                  },
                ],
              },
            },
          ]}
        />

        {showCreateDialog && <CategoryFormDialog onClose={() => setShowCreateDialog(false)} />}
        {categoryToEdit && (
          <CategoryFormDialog
            category={{
              id: categoryToEdit.id,
              name: categoryToEdit.name,
              description: categoryToEdit.description ?? null,
              resolverTeamId: String(categoryToEdit.resolverTeamId),
              assignmentMode: categoryToEdit.assignmentMode,
              defaultAssigneeId: categoryToEdit.defaultAssigneeId
                ? String(categoryToEdit.defaultAssigneeId)
                : null,
              priorityMode: categoryToEdit.priorityMode,
              fixedPriorityLevel: categoryToEdit.fixedPriorityLevel ?? null,
              slaMode: categoryToEdit.slaMode,
              slaFixedHours: categoryToEdit.slaFixedHours ?? null,
              slaCriticalHours: categoryToEdit.slaCriticalHours ?? null,
              slaHighHours: categoryToEdit.slaHighHours ?? null,
              slaMediumHours: categoryToEdit.slaMediumHours ?? null,
              slaLowHours: categoryToEdit.slaLowHours ?? null,
              pauseSlaOnWaiting: categoryToEdit.pauseSlaOnWaiting ?? false,
              aiSuggestionsMode: categoryToEdit.aiSuggestionsMode ?? 'automatic',
            }}
            onClose={() => setCategoryToEdit(null)}
          />
        )}
        {categoryToDelete && (
          <DeleteCategoryDialog
            category={categoryToDelete}
            onClose={() => setCategoryToDelete(null)}
          />
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default memo(TicketCategoriesSection)
